/**
 * Serac pricing section — the flat tier ladder (Free / Solo / Pro /
 * Team & Enterprise), rendered from TIER_CARDS via the modules.json manifest.
 *
 * Pricing model:
 *   - Free: a full tier, not a demo. The whole write-capable agent on one
 *     ServiceNow instance with 7-day history, plus the Apache License 2.0
 *     ServiceNow MCP server run by your own agent (Claude Code, opencode, any
 *     MCP client) — no carve-outs (fork it, self-host it, build a service on it).
 *   - Solo €19 / Pro €49: flat bundles. The wall is SCALE — instances,
 *     history, seats — not capability.
 *   - Team & Enterprise: sales-priced, no self-serve checkout.
 *   - Trial: 30-day reverse-trial — full Pro, no card or mandate;
 *     falls back to the Free plan automatically when it ends
 *     (data preserved, over-cap instances locked not deleted).
 *
 * À-la-carte module purchasing is retired (ALACARTE_BILLING_ENABLED = false in
 * routes/billing.ts). The one grid still on this page sells governance modules,
 * and it only renders while such a module is actually purchasable — see
 * `alaCarteGroups`.
 *
 * Authoritative prices and tier copy live in
 * portal/backend/src/utils/modules.ts (MODULE_CATALOG + TIER_CARDS). Trial
 * mechanics in portal/backend/src/utils/constants.ts (TRIAL_DAYS=30) and
 * portal/backend/src/services/mollie-service.ts.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon } from '../serac';
import { useIsMobile } from '../../hooks/useMediaQuery';

interface SeracPricingSectionProps {
  /** Skip the centered eyebrow + title (the /pricing page renders its own header). */
  bare?: boolean;
}

type Period = 'monthly' | 'yearly';

interface Tier {
  name: string;
  monthly: string;
  yearly: string;
  unit: string;
  seatCopy: string;
  tagline: string;
  cta: string;
  variant: 'ghost' | 'ice';
  highlight?: boolean;
  badge?: string;
  features: string[];
  onClick?: () => void;
}

/**
 * Module shape served from /modules.json. The file is generated by
 * portal/scripts/generate-modules-json.ts (runs as `prebuild` on every
 * frontend build) and reads MODULE_CATALOG from the canonical
 * portal/backend/src/utils/modules.ts. So names, descriptions, prices,
 * and tool counts here always match what the billing backend charges —
 * no hand-maintained duplicate.
 */
interface ManifestModule {
  id: string;
  name: string;
  description: string;
  category: 'integration' | 'feature' | 'governance' | 'support' | 'tier' | string;
  monthlyPriceCents: number;
  yearlyPriceCents: number;
  isPerSeat: boolean;
  isBase: boolean;
  toolCount: number;
}

interface ManifestCategory {
  id: string;
  label: string;
  description: string;
  order: number;
}

interface ManifestTier {
  id: string;
  name: string;
  monthlyLabel: string;
  yearlyLabel: string;
  monthlyUnit: string;
  yearlyUnit: string;
  seatCopy: string;
  tagline: string;
  ctaLabel: string;
  ctaAction: 'github-clone' | 'checkout' | 'signup' | 'contact';
  variant: 'ghost' | 'ice';
  highlight: boolean;
  badge?: string;
  features: string[];
}

interface ManifestData {
  modules: ManifestModule[];
  categories?: ManifestCategory[];
  tiers?: ManifestTier[];
}

// Marketing-side fallback if the static manifest can't be fetched
// (network blip, stale browser cache). Mirrors CATEGORY_METADATA in
// portal/backend/src/utils/modules.ts. Filter to the four module
// categories we render — `base`/`tier` are never grid sections here.
const FALLBACK_GROUPS: ManifestCategory[] = [
  { id: 'integration', label: 'Integrations', description: 'Connect your existing tools to ServiceNow', order: 0 },
  { id: 'feature', label: 'Features', description: 'Extend your development workflow', order: 1 },
  { id: 'governance', label: 'Governance', description: 'Governance control plane — policy, audit, and multi-instance orchestration', order: 2 },
  { id: 'support', label: 'Support', description: 'Get the help you need', order: 3 },
];

const formatPrice = (cents: number): string => {
  const euros = cents / 100;
  // Show whole euros without trailing .00; keep cents otherwise (e.g. €0.99).
  return Number.isInteger(euros) ? `€${euros}` : `€${euros.toFixed(2)}`;
};

export default function SeracPricingSection({ bare }: SeracPricingSectionProps = {}) {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('monthly');
  const isMobile = useIsMobile();

  // Fetch the canonical module manifest. We tolerate a fetch error
  // gracefully — falling back to inline minimums for the tier cards
  // and hiding the module grid.
  const [manifest, setManifest] = useState<ManifestData | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/modules.json', { cache: 'default' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setManifest({
          modules: Array.isArray(data.modules) ? data.modules : [],
          categories: Array.isArray(data.categories) ? data.categories : undefined,
          tiers: Array.isArray(data.tiers) ? data.tiers : undefined,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const orderedGroups: ManifestCategory[] = (() => {
    const src = manifest?.categories && manifest.categories.length > 0 ? manifest.categories : FALLBACK_GROUPS;
    return src
      .filter((c) => c.id !== 'base' && c.id !== 'tier')
      .slice()
      .sort((a, b) => a.order - b.order);
  })();

  // Group modules by category, drop the tier (rendered as the Hosted
  // card above) and any unknown categories. Sort within each group by
  // monthly price ascending — matches the existing visual order.
  const groupedModules: Record<string, ManifestModule[]> | null = (() => {
    if (!manifest) return null;
    const knownCategoryIds = new Set(orderedGroups.map((c) => c.id));
    const groups: Record<string, ManifestModule[]> = {};
    for (const m of manifest.modules) {
      if (m.category === 'tier' || m.isBase) continue;
      if (!knownCategoryIds.has(m.category)) continue;
      (groups[m.category] ||= []).push(m);
    }
    for (const k of Object.keys(groups)) {
      groups[k].sort((a, b) => a.monthlyPriceCents - b.monthlyPriceCents);
    }
    return groups;
  })();

  // The à-la-carte block below sells governance only — integrations, Review
  // Agent, Code Intelligence and Instance Graph are baked into the flat
  // Solo/Pro tiers. Compute the groups it would render BEFORE deciding to
  // render its frame: with policy-engine, multi-instance, ai-audit-trail and
  // skill-marketplace hidden, the group is empty, and an empty
  // "Team & Enterprise governance" frame promises a catalogue that isn't there.
  const alaCarteGroups: ManifestCategory[] = groupedModules
    ? orderedGroups.filter((g) => g.id === 'governance' && (groupedModules[g.id]?.length ?? 0) > 0)
    : [];

  const tiers: Tier[] = (manifest?.tiers && manifest.tiers.length > 0 ? manifest.tiers : []).map((t) => ({
    name: t.name,
    monthly: t.monthlyLabel,
    yearly: t.yearlyLabel,
    unit: period === 'monthly' ? t.monthlyUnit : t.yearlyUnit,
    seatCopy: t.seatCopy,
    tagline: t.tagline,
    cta: t.ctaLabel,
    variant: t.variant,
    highlight: t.highlight,
    badge: t.badge,
    features: t.features,
    onClick: () => {
      if (t.ctaAction === 'github-clone') {
        window.location.href = 'https://github.com/serac-labs/serac';
        return;
      }
      if (t.ctaAction === 'signup') {
        navigate('/login');
        return;
      }
      if (t.ctaAction === 'contact') {
        window.location.href = 'mailto:sales@serac.build?subject=Serac%20Team%20%26%20Enterprise';
        return;
      }
      // Carry the tier the visitor actually clicked. Without it every card
      // landed on the same à-la-carte summary — the €49 Pro card confirmed
      // "After trial: €0.00/mo", because Checkout re-derived the price from the
      // base module and an empty module list instead of from the card.
      //
      // Seats / stakeholders / modules are deliberately NOT passed: a flat tier
      // bundles its own seats (TIER_PRESETS.includedSeats — Pro is 3, not the
      // 1 this URL used to assert) and its own modules. Checkout drops them for
      // a tier anyway, which matters because the API 400s a tier request that
      // carries any module other than `base` (routes/billing.ts, the
      // "a tier is a fixed bundle" guard).
      // Checkout must run on the dashboard host. nginx proxies /api only for
      // dashboard.serac.build, so on the apex every portalApi call resolves to
      // the SPA's own index.html: `getPricing()` parses HTML as JSON, the tier
      // card is never found, and the page renders "That plan isn't available
      // for self-service checkout" — which it also did before this CTA carried
      // a tier at all, as the €0.00 summary the tier fix replaced.
      // Same host-hop the hero's CTAs already do (MarketingHome onStartFree).
      const path = `/checkout?tier=${encodeURIComponent(t.id)}&interval=${period}`;
      if (typeof window !== 'undefined' && /^(www\.)?serac\.build$/.test(window.location.hostname)) {
        window.location.href = `https://dashboard.serac.build${path}`;
        return;
      }
      navigate(path);
    },
  }));

  const sectionStyle: CSSProperties = bare
    ? { padding: isMobile ? '20px 16px 56px' : '32px 32px 80px', borderTop: '1px solid var(--border)' }
    : { padding: isMobile ? '56px 16px' : '80px 32px', borderTop: '1px solid var(--border)' };

  return (
    <section id="pricing" style={sectionStyle}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {!bare && (
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="t-eyebrow" style={{ marginBottom: 16 }}>
              PRICING
            </div>
            <h2 className="t-display" style={{ fontSize: 'clamp(28px, 5vw, 44px)', margin: '0 0 12px', lineHeight: 1.15 }}>
              Capability baked in. Scale when you grow.
            </h2>
            <p
              style={{
                fontSize: 16,
                color: 'var(--fg-muted)',
                maxWidth: 620,
                margin: '0 auto',
                lineHeight: 1.55,
              }}
            >
              Free starts you on one ServiceNow instance with the full agent. Solo (€19) and
              Pro (€49) are flat bundles — unlimited instances, full history and the consultant
              toolkit, all included. Team &amp; Enterprise is a shared workspace on a contract:
              talk to us.
            </p>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
          <div
            style={{
              display: 'inline-flex',
              background: 'var(--ink-100)',
              border: '1px solid var(--border)',
              borderRadius: 999,
              padding: 3,
            }}
          >
            {(['monthly', 'yearly'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                style={{
                  padding: isMobile ? '6px 12px' : '6px 16px',
                  borderRadius: 999,
                  fontSize: isMobile ? 11 : 12,
                  color: period === p ? 'var(--fg)' : 'var(--fg-muted)',
                  background: period === p ? 'var(--ink-200)' : 'transparent',
                  transition: 'all 150ms',
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.04em',
                  border: 'none',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {p.toUpperCase()}
                {p === 'yearly' && (isMobile ? ' · 1 MO FREE' : ' · 1 MONTH FREE')}
              </button>
            ))}
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))',
            gap: 16,
            maxWidth: 1160,
            margin: '0 auto',
          }}
        >
          {tiers.map((t, i) => {
            const price = period === 'monthly' ? t.monthly : t.yearly;
            return (
              <div
                key={i}
                style={{
                  background: t.highlight
                    ? 'linear-gradient(180deg, var(--ink-100) 0%, var(--ink-050) 100%)'
                    : 'var(--ink-050)',
                  border: t.highlight
                    ? '1px solid oklch(0.40 0.10 205)'
                    : '1px solid var(--border)',
                  borderRadius: 14,
                  padding: isMobile ? 20 : 28,
                  position: 'relative',
                  boxShadow: t.highlight
                    ? '0 0 40px -10px oklch(0.5 0.14 205 / 0.3)'
                    : 'none',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {t.badge && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -10,
                      left: 28,
                      padding: '3px 10px',
                      background: 'var(--ice-500)',
                      color: 'var(--ice-on-fill)',
                      borderRadius: 999,
                      fontSize: 11,
                      fontFamily: 'var(--font-mono)',
                      letterSpacing: '0.06em',
                      fontWeight: 500,
                    }}
                  >
                    {t.badge}
                  </div>
                )}
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--fg-muted)',
                    fontFamily: 'var(--font-mono)',
                    letterSpacing: '0.06em',
                  }}
                >
                  {t.name.toUpperCase()}
                </div>
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span className="t-display" style={{ fontSize: 'clamp(34px, 6vw, 44px)' }}>
                    {price}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--fg-dim)',
                    fontFamily: 'var(--font-mono)',
                    marginTop: 4,
                  }}
                >
                  {t.unit}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--ice-300)',
                    fontFamily: 'var(--font-mono)',
                    marginTop: 8,
                    marginBottom: 14,
                  }}
                >
                  {t.seatCopy}
                </div>
                <div
                  style={{
                    fontSize: 14,
                    color: 'var(--fg-muted)',
                    lineHeight: 1.55,
                    minHeight: 64,
                  }}
                >
                  {t.tagline}
                </div>
                <Button
                  variant={t.variant}
                  size="md"
                  style={{ width: '100%', marginTop: 8, marginBottom: 20 }}
                  onClick={t.onClick}
                >
                  {t.cta}
                </Button>
                <div style={{ height: 1, background: 'var(--border)', marginBottom: 16 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {t.features.map((f, j) => (
                    <div
                      key={j}
                      style={{
                        display: 'flex',
                        gap: 10,
                        fontSize: 13,
                        color: 'var(--fg-muted)',
                      }}
                    >
                      <span style={{ color: 'var(--ice-400)', marginTop: 2 }}>
                        <Icon.check s={12} />
                      </span>
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* À-la-carte governance grid. The whole frame — heading included —
            hangs on alaCarteGroups: no purchasable governance module means no
            frame, rather than a headline over an empty box. */}
        {alaCarteGroups.length > 0 && (
        <div
          style={{
            marginTop: 32,
            padding: isMobile ? 18 : 28,
            background: 'var(--ink-050)',
            border: '1px dashed var(--border-strong)',
            borderRadius: 14,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              marginBottom: 24,
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            <div>
              <div className="t-eyebrow" style={{ marginBottom: 6 }}>
                TEAM &amp; ENTERPRISE GOVERNANCE
              </div>
              <div style={{ fontSize: 18, color: 'var(--fg)', fontWeight: 500 }}>
                Org-level governance, composed à la carte for teams and organisations.
              </div>
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--fg-dim)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              {period === 'monthly' ? 'monthly' : 'yearly'} pricing · per seat unless noted
            </div>
          </div>

          {groupedModules &&
            alaCarteGroups.map((group) => {
              const items = groupedModules[group.id];
              return (
                <div key={group.id} style={{ marginBottom: 22 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--fg-dim)',
                      fontFamily: 'var(--font-mono)',
                      letterSpacing: '0.1em',
                      marginBottom: 10,
                    }}
                  >
                    {group.label.toUpperCase()}
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                      gap: 12,
                    }}
                  >
                    {items.map((m) => {
                      const price =
                        period === 'monthly'
                          ? formatPrice(m.monthlyPriceCents)
                          : formatPrice(m.yearlyPriceCents);
                      return (
                        <div
                          key={m.id}
                          style={{
                            padding: 16,
                            borderRadius: 10,
                            background: 'var(--ink-000)',
                            border: '1px solid var(--border)',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: 6,
                              alignItems: 'baseline',
                              gap: 8,
                            }}
                          >
                            <div style={{ fontSize: 14, color: 'var(--fg)', fontWeight: 500 }}>
                              {m.name}
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                color: 'var(--ice-300)',
                                fontFamily: 'var(--font-mono)',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {price}
                              <span
                                style={{
                                  color: 'var(--fg-dim)',
                                  marginLeft: 4,
                                  fontSize: 11,
                                }}
                              >
                                / {m.isPerSeat ? 'seat' : 'workspace'}
                              </span>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: 'var(--fg-muted)',
                              lineHeight: 1.5,
                            }}
                          >
                            {m.description}
                            {m.toolCount > 0 && (
                              <span
                                style={{
                                  color: 'var(--fg-dim)',
                                  fontFamily: 'var(--font-mono)',
                                  marginLeft: 6,
                                }}
                              >
                                · {m.toolCount} tools
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </div>
        )}
      </div>
    </section>
  );
}
